"use client";

import { Core } from "@faire/design-tokens";
import { buildElementLoggingData } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/EventLogging/buildLoggingData";
import { SDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import { ActionLoggingEventParams } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIAvatarElementRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Avatar/SDUIAvatarElementRenderer";
import { SDUIImageElementRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Image/SDUIImageElementRenderer";
import { SDUIRichTextRenderer } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/RichText/SDUIRichTextRenderer";
import { SDUITileInstanceRendererProps } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { useProductTileViewImpression } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileViewImpression";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer-visitor-shared/lib/verticalAspectRatio";
import { Button } from "@faire/web--source/slate/Button";
import { Close } from "@faire/web--source/slate/icons/Close";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { IBrandInsightsTile } from "@faire/web-api--source/indigofair/data/IBrandInsightsTile";
import { IImageElement } from "@faire/web-api--source/indigofair/data/IImageElement";
import React from "react";

export const BrandInsightsTileContents = React.memo(
  ({
    tileElement,
    loggingData,
    onMuteClick,
  }: SDUITileInstanceRendererProps & {
    onMuteClick: () => void;
  }) => {
    const { on_click, brand_insights_tile } = tileElement;
    const { brand_avatar, title, products, metadata, brand_title_on_click } =
      brand_insights_tile ?? {};
    if (
      !brand_avatar ||
      !title ||
      !products?.length ||
      !metadata?.brand_token
    ) {
      return null;
    }

    return (
      <SDUIOnClickHandler
        onClickActions={on_click}
        loggingData={loggingData}
        logLegacyClickEvent
        preventHandlingNestedClickEvents
        className="border-core-border-muted flex h-full w-full flex-col gap-4 border p-4"
      >
        <Row align="start" justify="space-between" gap={12}>
          <SDUIOnClickHandler
            onClickActions={brand_title_on_click ?? []}
            loggingData={ActionLoggingEventParams.build({
              ...loggingData,
              action_instance_identifier: metadata.brand_token,
            })}
            logLegacyClickEvent
            className="flex items-center gap-2"
          >
            <SDUIAvatarElementRenderer
              avatarElement={brand_avatar}
              loggingData={loggingData}
            />
            <Typography maxLines={2} as="span">
              <SDUIRichTextRenderer richTextElement={title} />
            </Typography>
          </SDUIOnClickHandler>
          <Button
            aria-label="Mute brand"
            variant="tertiary"
            onClick={onMuteClick}
          >
            <Close
              color={Core.icon.subdued}
              aria-label="Close button to mute brand updates"
            />
          </Button>
        </Row>
        <Row gap="1x">
          {products.map((simpleProductTile) =>
            simpleProductTile.image ? (
              <SimpleInsightsTileProduct
                key={
                  simpleProductTile.product_token ??
                  "brand-insights-see-more-tile"
                }
                simpleProductTile={simpleProductTile}
                loggingData={loggingData}
              />
            ) : null
          )}
        </Row>
      </SDUIOnClickHandler>
    );
  }
);

const SimpleInsightsTileProduct = ({
  simpleProductTile,
  loggingData,
}: {
  simpleProductTile: IBrandInsightsTile.IInsightProduct;
  loggingData: SDUITileInstanceRendererProps["loggingData"];
}) => {
  // if product_token is undefined, it's the "see more" tile and should not be counted as a product impression
  const isProductTile = simpleProductTile.product_token !== undefined;

  const { ref: trackProductTileViewRef, ...restTrackProductTileViewProps } =
    useProductTileViewImpression({
      productToken: simpleProductTile.product_token ?? "",
      impressionId: simpleProductTile.impression_id ?? "",
    });

  const productImageElement = simpleProductTile.image ?? IImageElement.build();

  return (
    <div
      className="relative h-full w-[33%]"
      data-test-id={`brand-insights-tile-product-${simpleProductTile.product_token}`}
      ref={isProductTile ? trackProductTileViewRef : undefined}
      {...(isProductTile ? restTrackProductTileViewProps : {})}
    >
      <SDUIImageElementRenderer
        imageElement={productImageElement}
        imageStyles={{
          aspectRatio: VERTICAL_ASPECT_RATIO,
          height: "100%",
          width: "100%",
        }}
        optimizeImageOptions={{ width: INSIGHTS_TILE_PRODUCT_IMAGE_MAX_WIDTH }}
        loggingData={buildElementLoggingData(
          loggingData,
          productImageElement.logging_data
        )}
      />
    </div>
  );
};

const INSIGHTS_TILE_PRODUCT_IMAGE_MAX_WIDTH = 128;
